import axios, { AxiosError, InternalAxiosRequestConfig } from "axios";
import { handleApiError, logError } from "utils/error-handler";

declare module "axios" {
  export interface InternalAxiosRequestConfig {
    _retry?: boolean;
  }
}

// Flag pour éviter les appels en boucle de refresh
let isRefreshing = false;
let failedQueue: Array<{
  resolve: (value?: unknown) => void;
  reject: (reason?: any) => void;
}> = [];

const processQueue = (error: Error | null, token: string | null = null) => {
  failedQueue.forEach((promise) => {
    if (error) {
      promise.reject(error);
    } else {
      promise.resolve(token);
    }
  });
  failedQueue = [];
};

// Fonction simplifiée pour le refresh token
const refreshTokenRequest = async (currentToken: string) => {
  const response = await axios.post(
    `${process.env.REACT_APP_HOPELAPP_API}/admins/refresh`,
    {},
    {
      headers: {
        Authorization: `Bearer ${currentToken}`,
        "Content-Type": "application/json",
      },
    }
  );
  return response.data;
};

/**
 * Get auth token from localStorage
 */
const getAuthToken = (): string | null => {
  return localStorage.getItem("accessToken");
};

/**
 * Create axios instance with base configuration
 */
const api = axios.create({
  baseURL: process.env.REACT_APP_HOPELAPP_API,
  timeout: 30000,
  headers: {
    "Content-Type": "application/json",
  },
});

/**
 * Request interceptor to add authentication token
 */
api.interceptors.request.use(
  async (config: InternalAxiosRequestConfig) => {
    try {
      const token = getAuthToken();
      if (token) {
        config.headers["Authorization"] = `Bearer ${token}`;
      }
      return config;
    } catch (error) {
      logError(error, "Auth token retrieval");
      return Promise.reject(error);
    }
  },
  (error) => {
    logError(error, "Request setup");
    return Promise.reject(error);
  }
);

/**
 * Response interceptor with simplified refresh token logic
 */
api.interceptors.response.use(
  (response) => {
    return response.data;
  },
  async (error: AxiosError) => {
    const originalRequest = error.config;

    if (!originalRequest) {
      return Promise.reject(error);
    }

    // Gérer uniquement les erreurs 401 qui ne sont pas des requêtes de refresh
    if (
      error.response?.status === 401 &&
      !originalRequest.url?.includes("refresh") &&
      originalRequest._retry !== true
    ) {
      if (isRefreshing) {
        try {
          const token = await new Promise((resolve, reject) => {
            failedQueue.push({ resolve, reject });
          });
          originalRequest.headers["Authorization"] = `Bearer ${token}`;
          return api(originalRequest);
        } catch (err) {
          return Promise.reject(err);
        }
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        const currentToken = getAuthToken();
        if (!currentToken) {
          throw new Error("No access token found");
        }

        // Refresh avec le token actuel
        const response = await refreshTokenRequest(currentToken);

        // Mettre à jour le stockage avec les nouveaux tokens
        const expirationTime = Date.now() + response.tokens.expiresIn * 1000;
        localStorage.setItem("accessToken", response.tokens.accessToken);
        localStorage.setItem("idToken", response.tokens.idToken);
        localStorage.setItem("tokenExpiresIn", expirationTime.toString());

        // Mettre à jour le header d'autorisation
        api.defaults.headers.common["Authorization"] = `Bearer ${response.tokens.accessToken}`;
        originalRequest.headers["Authorization"] = `Bearer ${response.tokens.accessToken}`;

        // Traiter la file d'attente
        processQueue(null, response.tokens.accessToken);
        return api(originalRequest);
      } catch (refreshError: any) {
        processQueue(refreshError as Error, null);

        // Nettoyer les données d'auth en cas d'échec
        localStorage.removeItem("accessToken");
        localStorage.removeItem("idToken");
        localStorage.removeItem("tokenExpiresIn");
        localStorage.removeItem("userId");
        delete api.defaults.headers.common["Authorization"];

        // Rediriger vers la page de connexion seulement si pas déjà sur une page d'auth
        if (!window.location.pathname.startsWith("/authentication")) {
          window.location.href = "/authentication/sign-in";
        }

        return Promise.reject(new Error("Token refresh failed"));
      } finally {
        isRefreshing = false;
      }
    }

    // Traiter les autres erreurs
    const appError = handleApiError(error);
    logError(appError, "API Response");

    // Gérer les autres codes d'erreur
    if (error.response?.status === 403) {
      console.warn("Access forbidden - insufficient permissions");
      // Log security event for audit trail
      console.warn("Security Event: Access denied", {
        url: originalRequest?.url,
        method: originalRequest?.method,
        status: error.response.status,
        message: (error.response.data as any)?.message,
        timestamp: new Date().toISOString(),
      });
    } else if (error.response?.status && error.response.status >= 500) {
      console.error("Server error occurred");
    }

    return Promise.reject(error.response?.data || error);
  }
);

export default api;
